// Shared per-word text-style model. Mirrors backend animation_engine._effective_style
// so the browser preview and the server export produce the same result.
//
// Inheritance: Brand Preset -> global/layer default (config) -> per-word override (config.wordStyles[wordId]).

export const STYLE_FIELDS = [
  "fillColor",
  "strokeEnabled", "strokeColor", "strokeWidth",
  "shadowEnabled", "shadowColor", "shadowDx", "shadowDy", "shadowBlur", "shadowOpacity",
  "glowEnabled", "glowColor", "glowBlur", "glowOpacity",
  "threeDEnabled", "threeDColor", "threeDDepth",
  "bgEnabled", "bgType", "bgColor", "bgColor2", "bgAngle", "bgOpacity", "bgPaddingX", "bgPaddingY", "bgRadius", "bgMode",
  "fontFamily", "fontSize", "fontWeight", "fontStyle", "opacity", "animation",
  "fillGradientEnabled", "fillGradientFrom", "fillGradientTo", "fillGradientAngle",
  "align", "textCase", "letterSpacing",
];

const first = (...vals) => vals.find((v) => v !== undefined && v !== null);

// Canonical base style from the global config (legacy keys honoured).
export function baseStyle(config, isHighlighted) {
  const c = config || {};
  return {
    fillColor: first(c.fillColor, isHighlighted ? c.highlightColor : c.textColor, "#FFFFFF"),
    strokeEnabled: first(c.strokeEnabled, c.outline, true),
    strokeColor: first(c.strokeColor, c.outlineColor, "#000000"),
    strokeWidth: first(c.strokeWidth, c.outlineWidth, 6),
    shadowEnabled: first(c.shadowEnabled, c.shadow, false),
    shadowColor: first(c.shadowColor, "#000000"),
    shadowDx: first(c.shadowDx, 3),
    shadowDy: first(c.shadowDy, 4),
    shadowBlur: first(c.shadowBlur, 4),
    shadowOpacity: first(c.shadowOpacity, 0.6),
    glowEnabled: first(c.glow, false),
    glowColor: first(c.glowColor, "#00F0FF"),
    glowBlur: first(c.glowBlur, 16),
    glowOpacity: first(c.glowOpacity, 0.85),
    threeDEnabled: first(c.threeD, false),
    threeDColor: first(c.threeDColor, "#000000"),
    threeDDepth: first(c.threeDDepth, 6),
    bgEnabled: first(c.background, false),
    bgType: first(c.bgType, "solid"),
    bgColor: first(c.bgColor, "#000000"),
    bgColor2: first(c.bgColor2, "#8B5CF6"),
    bgAngle: first(c.bgAngle, 90),
    bgOpacity: first(c.bgOpacity, 0.6),
    bgPaddingX: first(c.bgPaddingX, 0.35),
    bgPaddingY: first(c.bgPaddingY, 0.2),
    bgRadius: first(c.bgRadius, 0.35),
    bgMode: first(c.bgMode, "container"),
    fontFamily: first(c.fontFamily, "Montserrat"),
    fontSize: first(c.fontSize, 90),
    fontWeight: first(c.fontWeight, 700),
    fontStyle: first(c.fontStyle, "normal"),
    opacity: 1,
    animation: first(c.animation, "pop"),
    fillGradientEnabled: first(c.fillGradientEnabled, false),
    fillGradientFrom: first(c.fillGradientFrom, "#00F0FF"),
    fillGradientTo: first(c.fillGradientTo, "#8B5CF6"),
    fillGradientAngle: first(c.fillGradientAngle, 90),
    align: first(c.align, "center"),
    textCase: first(c.textCase, "none"),
    letterSpacing: first(c.letterSpacing, 0),
  };
}

// Effective style for a word = base + its override.
export function resolveWordStyle(config, wordId, isHighlighted) {
  const base = baseStyle(config, isHighlighted);
  const ov = (config?.wordStyles || {})[wordId] || {};
  for (const k of STYLE_FIELDS) if (ov[k] !== undefined && ov[k] !== null) base[k] = ov[k];
  return base;
}

// Effective per-token style inside a caption object.
// Precedence: global base -> object/preset style -> per-word manual override (highest).
// A preset stamps a COMPLETE object style (all effect toggles explicit) so switching
// presets never leaks the old look; per-word manual edits still win over the preset.
// Mirrors backend animation_engine._obj_word_style so preview == export.
export function resolveObjWordStyle(config, objStyle, wordId, isHighlighted) {
  const st = baseStyle(config, isHighlighted);
  if (objStyle) for (const k of STYLE_FIELDS) if (objStyle[k] !== undefined && objStyle[k] !== null) st[k] = objStyle[k];
  const ov = (config?.wordStyles || {})[wordId] || {};
  for (const k of STYLE_FIELDS) if (ov[k] !== undefined && ov[k] !== null) st[k] = ov[k];
  return st;
}

// Map a canonical style patch onto the global config (legacy keys where applicable).
const CANON_TO_CONFIG = {
  fillColor: "textColor",
  strokeEnabled: "outline",
  strokeColor: "outlineColor",
  strokeWidth: "outlineWidth",
  shadowEnabled: "shadow",
  glowEnabled: "glow",
  threeDEnabled: "threeD",
  bgEnabled: "background",
};

export function applyGlobalStyle(config, patch) {
  const next = { ...config };
  for (const [k, v] of Object.entries(patch)) {
    // Write the CANONICAL key (what baseStyle/_base_style read first) AND its legacy alias,
    // so the two never diverge — a stale canonical key can no longer shadow a legacy write
    // (this was why the Stroke toggle / Width slider appeared frozen after applying a preset).
    next[k] = v;
    const legacy = CANON_TO_CONFIG[k];
    if (legacy) next[legacy] = v;
  }
  return next;
}

// Only the fields that differ from the inherited base (keeps overrides minimal).
export function diffOverride(base, edited) {
  const out = {};
  for (const k of STYLE_FIELDS) if (edited[k] !== undefined && edited[k] !== base[k]) out[k] = edited[k];
  return out;
}

// Non-destructive display-time case transform. MUST mirror backend animation_engine._apply_case
// so preview == export. The underlying transcript text is never modified.
export function applyCase(text, mode, idx = 0) {
  if (!text || !mode || mode === "none") return text;
  if (mode === "upper") return text.toUpperCase();
  if (mode === "lower") return text.toLowerCase();
  if (mode === "title") return text.slice(0, 1).toUpperCase() + text.slice(1).toLowerCase();
  if (mode === "sentence") return idx === 0 ? text.slice(0, 1).toUpperCase() + text.slice(1).toLowerCase() : text.toLowerCase();
  return text;
}
